import { Box, Card, CircularProgress, Typography } from '@mui/material';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { wazuhAPI } from 'src/api/mindtime/wazuhAPI';
import logger from 'src/utils/logger';
import CommandBlock from '../../deploy-agent/CommandBlock';
import { DEPLOY_STEP_TITLES } from '../../deploy-agent/deployAgentConstants';
import DeployStepSection from '../../deploy-agent/DeployStepSection';

const cardSx = {
  backgroundColor: '#fff',
  border: '1px solid #E2E8F0',
  borderRadius: '8px',
  boxShadow: 'none',
  boxSizing: 'border-box',
} as const;

type EndpointTechnicalDetailsTabProps = {
  systemWazuhAgentId: string;
};

const EndpointTechnicalDetailsTab: FC<EndpointTechnicalDetailsTabProps> = ({
  systemWazuhAgentId,
}) => {
  const agentId = systemWazuhAgentId.trim();
  const [loading, setLoading] = useState(Boolean(agentId));
  const [error, setError] = useState<string | null>(null);
  const [installCommands, setInstallCommands] = useState<string[]>([]);
  const [copiedMap, setCopiedMap] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!agentId) {
      setInstallCommands([]);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setCopiedMap({});

    wazuhAPI
      .getInstallationCommands(agentId)
      .then((data) => {
        if (cancelled) return;
        setInstallCommands(data?.installCommands ?? []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        logger(err, 'error', 'EndpointTechnicalDetailsTab.installCommands');
        setInstallCommands([]);
        setError(err instanceof Error ? err.message : 'Failed to load installation commands');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [agentId]);

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16, marginTop: 8, minWidth: 0 }}>
      <Card sx={cardSx} style={{ padding: 20 }}>
        <Typography
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: '#1E293B',
            marginBottom: 16,
          }}
        >
          Installation Commands
        </Typography>

        {loading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{ paddingTop: 32, paddingBottom: 32, minHeight: 120 }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Typography style={{ fontSize: 13, color: '#E53E3E' }}>{error}</Typography>
        ) : installCommands.length === 0 ? (
          <Typography style={{ fontSize: 13, color: '#94A3B8' }}>
            No installation commands available for this endpoint.
          </Typography>
        ) : (
          installCommands.map((cmd, idx) => {
            const isLast = idx === installCommands.length - 1;
            const title =
              installCommands.length === 1
                ? DEPLOY_STEP_TITLES[3]
                : idx === 0
                  ? DEPLOY_STEP_TITLES[3]
                  : idx === installCommands.length - 1
                    ? DEPLOY_STEP_TITLES[4]
                    : `Step ${idx + 1}`;

            return (
              <DeployStepSection
                key={`${title}-${cmd}`}
                stepNumber={idx + 1}
                title={title}
                complete={!!copiedMap[idx]}
                dimmed={idx > 0 && !copiedMap[idx - 1]}
                isLast={isLast}
              >
                <CommandBlock
                  command={cmd}
                  multiline={cmd.includes('\n')}
                  onCopy={() => setCopiedMap((prev) => ({ ...prev, [idx]: true }))}
                />
              </DeployStepSection>
            );
          })
        )}
      </Card>
    </Box>
  );
};

export default EndpointTechnicalDetailsTab;
